import React from 'react';
import { useSearchBarContext } from 'components/Filters/context/SearchBarContext/SearchBarContext';
import { Group, Text, Input, useBreakpoint } from '@landr/maestro';
import {
    inputStyles,
    groupItemStyles,
    textStyles,
    autocompleteStyles,
} from './styled';

const _TextInput = React.forwardRef(
    (
        {
            inputValue,
            inputValueLength,
            placeholder,
            predictiveInput,
            isDisabled = false,
            handleChange,
            handleFocus,
            handleKeyDown,
        }: {
            inputValue: string;
            inputValueLength: number;
            placeholder: string;
            predictiveInput: string;
            isDisabled?: boolean;
            handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
            handleFocus: () => void;
            handleKeyPress: (
                event: React.KeyboardEvent<HTMLInputElement>,
            ) => void;
            handleKeyDown: (
                event: React.KeyboardEvent<HTMLInputElement>,
            ) => void;
        },
        forwardedRef:
            | ((instance: HTMLInputElement | null) => void)
            | React.MutableRefObject<HTMLInputElement | null>
            | null,
    ) => {
        const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;
        const isMobile = useBreakpoint('md');

        return (
            <Group.Item
                className={groupItemStyles({
                    hasInput: Boolean(inputValueLength),
                })}
            >
                <Group
                    gap="none"
                    className={autocompleteStyles({
                        isMobile,
                    })}
                >
                    <Group.Item opacity="invisible">
                        <Text
                            color="text.medium"
                            className={textStyles({ isTouchDevice })}
                        >
                            {inputValue}
                        </Text>
                    </Group.Item>
                    <Group.Item>
                        <Text
                            color="text.medium"
                            className={textStyles({ isTouchDevice })}
                        >
                            {predictiveInput.substring(inputValue.length)}
                        </Text>
                    </Group.Item>
                </Group>
                <Input
                    ref={forwardedRef}
                    value={inputValue}
                    name="search"
                    placeholder={placeholder}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onFocus={handleFocus}
                    id="search-input"
                    type="search"
                    inputMode="search"
                    size="md"
                    height="auto"
                    isDisabled={isDisabled}
                    className={inputStyles({
                        hasValue: Boolean(inputValue),
                        isMobile,
                    })}
                />
            </Group.Item>
        );
    },
);

const TextInput = React.forwardRef(
    (
        _,
        forwardedRef:
            | ((instance: HTMLInputElement | null) => void)
            | React.MutableRefObject<HTMLInputElement | null>
            | null,
    ) => {
        const {
            inputRef,
            inputValue,
            placeholder,
            predictiveInput,
            handleFocus,
            handleKeyPress,
            handleKeyDown,
            onInputChange,
            isSearchBarDisabled,
        } = useSearchBarContext();

        return (
            <_TextInput
                ref={forwardedRef ?? inputRef}
                inputValue={inputValue}
                inputValueLength={inputValue.length}
                placeholder={predictiveInput ? '' : placeholder}
                handleChange={(
                    event: React.ChangeEvent<HTMLInputElement>,
                ): void => {
                    onInputChange(event.target.value);
                }}
                handleKeyPress={handleKeyPress}
                handleKeyDown={handleKeyDown}
                handleFocus={handleFocus}
                predictiveInput={predictiveInput}
                isDisabled={isSearchBarDisabled}
            />
        );
    },
);

export default TextInput;
